/**
 * Kanonisches Artikelmodell. Unabhängig von Shopify, JTL-FFN oder einem späteren
 * eigenen WMS. Adapter mappen von/zu diesem Modell, niemals direkt untereinander.
 *
 * Einheiten sind fest: Gewicht in kg, Maße in cm, Geld in Minor Units (Cent).
 */

export type ProductStatus = "draft" | "active" | "archived";

export interface Money {
  /** Betrag in kleinster Währungseinheit (z. B. Cent) */
  amountMinor: number;
  /** ISO-4217, z. B. "EUR" */
  currency: string;
}

export interface ProductIdentifiers {
  /** GTIN-8/12/13/14 (EAN/UPC). Wird für den Wareneingang/Scan im Lager dringend empfohlen. */
  gtin?: string;
  isbn?: string;
  asin?: string;
  /** Amazon-FNSKU (Pre-FBA) – geht als Attribut „FNSKU“ ans Lager */
  fnsku?: string;
  /** Herstellerartikelnummer */
  mpn?: string;
  manufacturer?: string;
}

export interface ProductDimensions {
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

export interface ProductWeight {
  netKg?: number;
  grossKg?: number;
}

/**
 * Importregel: liefert die Quelle (Shop, CSV, Lager) nur Brutto- ODER Nettogewicht, gilt der Wert für beide.
 * Sind beide gepflegt, bleiben sie unverändert.
 */
export function completeWeight(w: ProductWeight | undefined): ProductWeight | undefined {
  const g = w?.grossKg && w.grossKg > 0 ? w.grossKg : undefined;
  const n = w?.netKg && w.netKg > 0 ? w.netKg : undefined;
  if (g == null && n == null) return undefined;
  return { grossKg: g ?? n!, netKg: n ?? g! };
}

export interface ProductCustoms {
  /** Zolltarifnummer (HS/TARIC/KN), nur Ziffern */
  hsCode?: string;
  /** ISO-3166-1 alpha-2 */
  countryOfOrigin?: string;
  customsValue?: Money;
  /** Warenbeschreibung für Zollinhaltserklärung (englisch empfohlen) */
  customsDescription?: string;
}

export interface DangerousGoods {
  /** UN-Nummer, z. B. "UN3481" */
  unNumber: string;
  hazardClass?: string;
  limitedQuantity?: boolean;
}

export interface ProductHandling {
  batchManaged: boolean;
  bestBeforeManaged: boolean;
  serialManaged: boolean;
  fragile?: boolean;
  dangerousGoods?: DangerousGoods;
}

export interface BundleComponent {
  productId: string;
  sku: string;
  quantity: number;
}

/**
 * Gebinde (Verpackungseinheit mit eigener EAN), z. B. „Box of 20“ oder „Palette 480“ – wichtig für B2B, Einkauf und
 * Warenausgang. Im FFN „bundles“ am Artikel, in Odoo „Verpackungen“ (product.packaging).
 */
export interface ProductPackUnit {
  name: string;
  quantity: number;
  ean: string | null;
}

/** kleinstes Gebinde mit mehr als einem Stück – Ersatz für die Kartongröße, wenn keine gepflegt ist */
export const smallestPackUnit = (units: ProductPackUnit[] | undefined | null): ProductPackUnit | null =>
  [...(units ?? [])].filter((u) => u.quantity > 1).sort((a, b) => a.quantity - b.quantity)[0] ?? null;

/** Menge in Gebinden ausdrücken: 45 Stk bei „Box of 20“ → „2 × Box of 20 + 5 Stk“ */
export function packUnitBreakdown(quantity: number, units: ProductPackUnit[] | undefined | null): string | null {
  const list = [...(units ?? [])].filter((u) => u.quantity > 1).sort((a, b) => b.quantity - a.quantity);
  if (!list.length || quantity < list.at(-1)!.quantity) return null;
  let rest = quantity;
  const parts: string[] = [];
  for (const u of list) {
    const n = Math.floor(rest / u.quantity);
    if (n > 0) { parts.push(`${n} × ${u.name}`); rest -= n * u.quantity; }
  }
  if (rest > 0) parts.push(`${rest} Stk`);
  return parts.join(" + ");
}

export interface Product {
  id: string;
  tenantId: string;
  sku: string;
  name: string;
  description?: string;
  status: ProductStatus;
  identifiers: ProductIdentifiers;
  dimensions?: ProductDimensions;
  weight?: ProductWeight;
  customs: ProductCustoms;
  netRetailPrice?: Money;
  handling: ProductHandling;
  bundle?: { components: BundleComponent[] };
  /** Gebinde (Verpackungseinheiten mit eigener EAN) */
  packUnits?: ProductPackUnit[];
  imageUrls: string[];
  attributes: Record<string, string>;
}
